import React, { createContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import getQueryParam from '../utils/getQueryParam';
import axios from 'axios';
import { TError, TLocale } from 'models';
import { deleteCookie, getCookie, getCookieDomain, prepareAuthData, setCookie, translate } from '../utils';

type TOnError = (error: TError[]) => void;

const DEFAULT_LOCALE = 'pl';
const TENANT = getQueryParam('tenant') || window._env_.TENANT;
const BASE_API_URL = window._env_.API_IDENTIFIER_URL;
const GENERIC_ERROR = { code: '', message: 'Something goes wrong!' };
const REFRESH_TOKEN_BEFORE_EXPIRE_TIME = 300000; // 5 mins before expire time
export const TRUST_AUTH_EXCHANGE_PATH = '/exchange';

const AccountContext = createContext<{
  login:(username: string, password: string, onError: TOnError, onLoginChallenge: (challengeName: string) => void) => void,
  loginSSO: (onError: TOnError) => void,
  logout: () => void,
  resetPassword: (username: string, onError: TOnError, onSuccess: (data: unknown) => void) => void,
  updatePassword: (confirmation_code: string, password: string, username: string, onError: TOnError, onSuccess: () => void) => void,
  challengePassword: (password: string, onError: TOnError, onSuccess: () => void) => void,
  enableMfa: (code: string, onError: TOnError) => void,
  verifyMfa: (code: string, onError: TOnError) => void,
  completeSSOLogin: (authenticationResult: any) => void,
  locale: string,
  tenant: string,
  isAuthenticated: number,
  challengeData: {username: string, session: string, challenge_parameters?: Record<string, any>} | null,
  setLocale: (lang: TLocale) => void,
}>({
      login: (username: string, password: string, onError: TOnError, onLoginChallenge: (challengeName: string) => void) => {},
      loginSSO: (onError: TOnError) => {},
      completeSSOLogin: (authenticationResult: any) => {},
      logout: () => {},
      resetPassword: (username: string, onError: TOnError, onSuccess: (data: unknown) => void) => {},
      updatePassword: (confirmation_code: string, password: string, username: string, onError: TOnError, onSuccess: () => void) => {},
      challengePassword: (password: string, onError: TOnError, onSuccess: () => void) => {},
      enableMfa: (code: string, onError: TOnError) => {},
      verifyMfa: (code: string, onError: TOnError) => {},
      locale: DEFAULT_LOCALE,
      tenant: TENANT,
      isAuthenticated: -1,
      challengeData: null,
      setLocale: (lang: TLocale) => {},
    });

const Account = (props: { children: React.ReactNode }) => {
  useEffect(() => {

    //Check Login State
    if (window.location.pathname.replace(/\/+$/, '') === TRUST_AUTH_EXCHANGE_PATH) {
      // Trust auth exchange page stores new login itself, existing session must not interfere
      setLoggedIn(0);
    } else if (getQueryParam('logout')) {
      logout();
    } else if (getQueryParam('resetPassword')) {
      localStorage.removeItem('authentication_result');
      setLoggedIn(0);
    } else if (getCookie('authentication_result')) {
      preflightCheck();
    } else {
      setLoggedIn(0);
    }

    const lang = localStorage.getItem('locale');

    //Check language
    if (lang) {
      updateLocale(lang as TLocale);
      sendCurrentLocaleToParent(lang as TLocale);
    } else {
      setCurrentLocale(DEFAULT_LOCALE);
    }

    // eslint-disable-next-line
  }, []);

  const [loggedIn, setLoggedIn] = useState(-1);
  const [locale, updateLocale] = useState(localStorage.getItem('locale') || 'pl');
  const [challengeData, updateChallengeData] = useState<{username: string, session: string, challenge_parameters?: Record<string, any>} | null>(null);

  const storeLogin = (data: Record<string, any>) => {
    let broker = getQueryParam('broker');

    if (broker) {
      data.broker = broker;
    }

    setCookie('authentication_result', JSON.stringify(data), 365, getCookieDomain());

    window.parent?.postMessage(
      JSON.stringify({
        name: 'login',
        error: false,
        message: data,
      }),
      '*'
    );

    setLoggedIn(1);
  };

  const preflightCheck = () => {
    const now = new Date().getTime();

    const parsedAuthData = JSON.parse(getCookie('authentication_result') || '');

    if (!parsedAuthData) {
      logout();
    }

    const decodedToken: {exp: number, username: string} = jwtDecode(parsedAuthData.access_token);

    const expiresAt = decodedToken.exp * 1000; // Convert to the milliseconds
    const username = decodedToken.username;
    const refreshToken = parsedAuthData.refresh_token;
    const serverNow = now - (parsedAuthData.user_time_diff || 0);

    // Session without refresh token (e.g. trust auth) can't be prolonged, it ends when token expires
    if (!refreshToken) {
      if (!expiresAt || serverNow >= expiresAt) {
        logout();
      } else {
        storeLogin(parsedAuthData);
      }

      return;
    }

    if (!expiresAt || serverNow >= (expiresAt - REFRESH_TOKEN_BEFORE_EXPIRE_TIME)) {
      axios.post(`${BASE_API_URL}/token/refresh/${TENANT}`, {
        'username': username,
        'refresh_token': refreshToken,
      }, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      }).then(response => {
        const data = response.data;
        const authResults = {
          ...parsedAuthData,
          access_token: data?.authentication_result.access_token,
          id_token: data?.authentication_result.id_token,
        };
        storeLogin(prepareAuthData(authResults));
      }).catch(() => {
        logout();
      });
    } else {
      storeLogin(parsedAuthData || '');
    }
  };

  const login = (username: string, password: string, onError: TOnError, onLoginChallenge: (challengeName: string) => void) => {
    axios.post(`${BASE_API_URL}/login/${TENANT}`, {
      'username': username,
      'password': password
    }, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    }).then(response => {
      const data = response.data;
      if (data) {
        if (data.challenge_name) {
          updateChallengeData({
            username: username,
            session: data.data?.session,
            challenge_parameters: data.challenge_parameters
          });
          onLoginChallenge(data.challenge_name);
        } else if (data?.authentication_result && data?.authentication_result.access_token) {
          storeLogin(prepareAuthData(data?.authentication_result));
        } else {
          onError(setGenericError());
        }
      }
    }).catch(error => {
      if (error.response) {
        const data = error.response.data;
        if (data?.errors) {
          onError(data.errors);
        }
      } else {
        onError(setGenericError());
      }
    });
  };

  const loginSSO = (onError: TOnError) => {
    try {
      // Capture parent window URI for redirect after successful SSO authentication
      try {
        if (window.parent && window.parent !== window) {
          const parentUri = window.parent.location.href;
          localStorage.setItem('sso_parent_uri', parentUri);
        }
      } catch (crossOriginError) {
        // If we can't access parent due to cross-origin restrictions, try to get from document.referrer
        if (document.referrer) {
          localStorage.setItem('sso_parent_uri', document.referrer);
        }
      }

      // Import the SSO function dynamically to avoid circular imports
      import('../utils/sso').then(({ initiateSSOLogin }) => {
        initiateSSOLogin();
      });
    } catch (error) {
      console.error('SSO login error:', error);
      onError(setGenericError());
    }
  };

  const completeSSOLogin = (authenticationResult: any) => {
    // Use the same storeLogin method as normal login
    storeLogin(prepareAuthData(authenticationResult));
  };

  const resetPassword = (username: string, onError: TOnError, onSuccess: (data: unknown) => void) => {
    axios.post(`${BASE_API_URL}/password/reset/${TENANT}`, {
      'username': username,
    }, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    }).then(response => {
      const data = response.data;
      onSuccess(data);
    }).catch(error => {
      if (error.response) {
        const data = error.response.data;
        if (data?.errors) {
          onError(data.errors);
        }
      } else {
        onError(setGenericError());
      }
    });
  };

  const updatePassword = (confirmation_code: string, password: string, username: string, onError: TOnError, onSuccess: () => void) => {
    axios.post(`${BASE_API_URL}/password/update/${TENANT}`, {
      'confirmation_code': confirmation_code,
      'password': password,
      'username': username,
    }, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    }).then(() => {
      onSuccess();
    }).catch(error => {
      if (error.response) {
        const data = error.response.data;
        if (data?.errors) {
          onError(data.errors);
        }
      } else {
        onError(setGenericError());
      }
    });
  };

  const challengePassword = (password: string, onError: TOnError, onSuccess: () => void) => {
    axios.post(`${BASE_API_URL}/auth/challenge/${TENANT}`, {
      challenge_name: 'NEW_PASSWORD_REQUIRED',
      challenge_responses: {
        'USERNAME': challengeData?.username,
        'NEW_PASSWORD': password,
        'user_attributes': {
          'name': challengeData?.username
        }
      },
      session: challengeData?.session
    }, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    }).then(() => {
      onSuccess();
    }).catch(error => {
      if (error.response) {
        const data = error.response.data;
        if (data?.errors) {
          onError(data.errors);
        }
      } else {
        onError(setGenericError());
      }
    });
  };

  const enableMfa = (code: string, onError: TOnError) => {
    axios.post(`${BASE_API_URL}/auth/challenge/${TENANT}`, {
      challenge_name: 'INTERNAL_MFA_SETUP',
      challenge_responses: {
        USERNAME: challengeData?.username,
        software_token_mfa_code: code
      },
      session: challengeData?.session
    }, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    }).then((response) => {
      const data = response.data;

      if (data?.authentication_result && data?.authentication_result.access_token) {
        storeLogin(prepareAuthData(data?.authentication_result));
      } else {
        onError(setGenericError());
      }
    }).catch(error => {
      if (error.response) {
        const data = error.response.data;
        if (data?.errors) {
          onError(data.errors);
        }
      } else {
        onError(setGenericError());
      }
    });
  };

  const verifyMfa = (code: string, onError: TOnError) => {
    axios.post(`${BASE_API_URL}/auth/challenge/${TENANT}`, {
      challenge_name: 'SOFTWARE_TOKEN_MFA',
      challenge_responses: {
        USERNAME: challengeData?.username,
        software_token_mfa_code: code
      },
      session: challengeData?.session
    }, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    }).then((response) => {
      const data = response.data;

      if (data?.authentication_result && data?.authentication_result.access_token) {
        storeLogin(prepareAuthData(data?.authentication_result));
      } else {
        onError(setGenericError());
      }
    }).catch(error => {
      if (error.response) {
        const data = error.response.data;
        if (data?.errors) {
          onError(data.errors);
        }
      } else {
        onError(setGenericError());
      }
    });
  };

  const logout = () => {
    if (getCookie('authentication_result')) {
      const parsedAuthData = JSON.parse(getCookie('authentication_result') as string);
      axios.post(`${BASE_API_URL}/logout/${TENANT}`, {}, {
        headers: {
          'Authorization': `${parsedAuthData.token_type} ${parsedAuthData.access_token}`,
        },
      }).then().catch(error => console.error(error));
    }

    deleteCookie('authentication_result', getCookieDomain());
    setLoggedIn(0);
    window.parent?.postMessage(
      JSON.stringify({
        name: 'logout',
        error: false,
        message: 'successfully logout'
      }),
      '*'
    );
  };

  const setCurrentLocale = (lang: TLocale) => {
    localStorage.setItem('locale', lang);
    updateLocale(lang);
    sendCurrentLocaleToParent(lang);
  };

  const sendCurrentLocaleToParent = (lang: TLocale) => {
    window.parent?.postMessage(
      JSON.stringify({
        name: 'lang',
        error: false,
        message: lang
      }),
      '*'
    );
  };

  return (
    <AccountContext.Provider value={{
      login,
      loginSSO,
      logout,
      resetPassword,
      updatePassword,
      challengePassword,
      enableMfa,
      verifyMfa,
      completeSSOLogin,
      isAuthenticated: loggedIn,
      locale,
      tenant: TENANT,
      // @ts-ignore
      challengeData,
      setLocale: (lang: TLocale) => setCurrentLocale(lang)
    }}>
      {props.children}
    </AccountContext.Provider>
  );
};

const setGenericError = () => {
  if (translate({ key: 'sso.errors.default' })) {
    return [
      {
        ...GENERIC_ERROR,
        message: translate({ key: 'sso.errors.default' })
      }
    ];
  } else {
    return [GENERIC_ERROR];
  }
};

export { Account, AccountContext };
